import React from 'react';
import { PieChart } from '@mui/x-charts/PieChart';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import './PieChartCard.css';

/**
 * PieChartCard component.
 *
 * A dashboard card displaying a MUI PieChart with a custom colour-coded
 * legend below the chart. Each legend item shows a descriptive tooltip on
 * hover (powered by MUI Tooltip).
 *
 * When `isEmpty` is `true` a single light-gray placeholder slice fills the
 * chart and the legend is hidden.
 *
 * Visual style matches the StatCard: white background, 16 px border-radius,
 * subtle shadow.
 *
 * @component
 * @param {object}  props
 * @param {string}  props.title           - Card title shown in the header row.
 * @param {Array<{
 *   id:          number,
 *   value:       number,
 *   label:       string,
 *   color:       string,
 *   description?: string
 * }>} [props.data=[]]                    - Array of pie-slice descriptor objects.
 * @param {boolean} [props.isEmpty=false] - When `true` the chart renders a gray placeholder slice
 *                                          and the legend is hidden.
 * @param {boolean} [props.loading=false] - When `true` a shimmer skeleton is shown instead of
 *                                          the chart.
 * @returns {JSX.Element}
 */
const PieChartCard = ({
    title,
    data = [],
    isEmpty = false,
    loading = false,
}) => {
    const total = isEmpty ? null : data.reduce((sum, item) => sum + item.value, 0);
    const getPercent = (value) => total > 0 ? Math.round((value / total) * 100) : 0;
    const { t } = useTranslation();
    return (
        <div className="pcc-card">

            {/* ── Row 1: title ── */}
            <div className="pcc-header">
                <span className="pcc-title">{title}</span>
            </div>

            {/* ── Row 2: chart + legend side by side ── */}
            <div className="pcc-body">
                {loading ? (
                    <div className="pcc-skeleton" />
                ) : (
                    <>
                        <div className="pcc-chart-wrap">
                            <PieChart
                                series={[{
                                    data,
                                    arcLabel: isEmpty ? 'label' : (item) => item.value >= 1 ? `${item.value}` : '',
                                    innerRadius: isEmpty ? 0 : 45,
                                    cornerRadius: isEmpty ? 0 : 6,
                                }]}
                                slotProps={{ legend: { hidden: true } }}
                                sx={{ '& .MuiPieArcLabel-root': { fontSize: '0.7rem' } }}
                                margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
                                height={150}
                            />
                            {!isEmpty && (
                                <div className="pcc-center-label">
                                    <span className="pcc-center-value">{total}</span>
                                    <span className="pcc-center-sub">{t("Total")}</span>
                                </div>
                            )}
                        </div>
                        {!isEmpty && (
                            <div className="pcc-legend">
                                {data.map(item => (
                                    <Tooltip
                                        key={item.id}
                                        title={item.description ?? ''}
                                        placement="right"
                                        arrow
                                    >
                                        <div className="pcc-legend-item">
                                            <span
                                                className="pcc-legend-dot"
                                                style={{ backgroundColor: item.color }}
                                            />
                                            <span className="pcc-legend-label">{item.label}</span>
                                            <span className="pcc-legend-pct">{getPercent(item.value)}%</span>
                                        </div>
                                    </Tooltip>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

        </div>
    );
};

export default PieChartCard;
